import { useState, useEffect } from 'react';
import { useMutation, useQuery } from 'react-apollo';
import uniqBy from 'lodash.uniqby';

import { checkout as checkoutMutation } from '../../../../../graphql/mutations/checkout';
import { EventAddonItems } from '../../../../../graphql/queries/events';
import { sentryCaptureMessage } from '../../../../../util/sentry';
import { MeQuery } from '../../../../../graphql/queries/account';
import useAuthenticated from '../../../../../hooks/useAuthenticated';
import { formatPriceForDisplay } from '../../../../../util/format-money';
import {
  AlbumFolderForContributor,
  AlbumFolderForGiftingEventAddonItems,
} from '../../../../../graphql/queries/album-folder';
import { getCelebratedPersonName } from '../../../views/AlbumFolder/FreemiumEvents/SpecialtyAlbumGreetingModal/helpers';
import { sortEventAddonItemsByPriority } from '../../../views/AlbumFolder/FreemiumEventCreateWizard/util';
import { gtm } from '../../../../../util/analytics';
import { EVENT_ADDON_ITEM_TYPE } from '../../../../../constants/event-items';
import { ALBUM_SUBTYPE } from '../../../../../constants/albums';

/**
 * General Event Addons hook
 *
 * @param {Object} options The options.
 * @param {Object?} options.account The Account object.
 * @param {String?} options.albumFolderId The albumFolder id.
 * @param {Array<String>?} options.defaultSelectedEventAddonItemIds List of event addon items to be pre-selected.
 * @param {Array<String>?} options.defaultSelectedEventAddonItemTypes List of event addon items types to be pre-selected.
 * @param {Object?} options.eventAddonItemsQueryVars Object containing variables regarding event addon items.
 * @param {Function} [options.onCheckoutSuccess] Method running on checkout success.
 * @param {Function} [options.onCheckoutError] Method running on checkout error.
 * @param {Boolean} options.excludePurchased Whether or not to exclude purchased addons from the gift ability
 * @returns {{onCheckout: ((function({token: *, albumFolderId?: *}=): Promise<void>)|*), submitting: boolean, checkoutError: string, onEventAddonItemToggle: onEventAddonItemToggle, success: boolean, eventAddonItems: *[], loadingEventAddonItems: boolean, selectedEventAddonItemIds: *[]}}
 */
const useGiftingEventAddonPurchase = ({
  account,
  onCheckoutSuccess,
  onCheckoutError,
  albumFolderId,
  defaultSelectedEventAddonItemIds = [],
  defaultSelectedEventAddonItemTypes = [],
  eventAddonItemsQueryVars = {},
  excludePurchased = false,
} = {}) => {
  const authenticated = useAuthenticated();
  const { data: { me: user } = {} } = useQuery(MeQuery, {
    skip: !authenticated,
  });
  const [submitting, setSubmitting] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [success, setSuccess] = useState(false);
  const [defaultTypesSet, setDefaultTypesSet] = useState(false);
  const [defaultIdsSet, setDefaultIdsSet] = useState(false);
  const [selectedEventAddonItemIds, setSelectedEventAddonItemIds] = useState(
    defaultSelectedEventAddonItemIds ?? [],
  );

  const {
    data: { eventAddonItems = [] } = {},
    loading: loadingEventAddonItems,
  } = useQuery(EventAddonItems, {
    variables: {
      ...eventAddonItemsQueryVars,
      hierarchy_item_id: albumFolderId,
    },
  });

  useEffect(() => {
    if (
      !defaultIdsSet &&
      eventAddonItems?.length > 0 &&
      defaultSelectedEventAddonItemIds?.length > 0
    ) {
      setSelectedEventAddonItemIds(
        eventAddonItems
          .filter(({ id }) => defaultSelectedEventAddonItemIds.includes(id))
          .map(({ id }) => id),
      );
      setDefaultIdsSet(true);
    }

    if (
      !defaultTypesSet &&
      eventAddonItems?.length > 0 &&
      defaultSelectedEventAddonItemTypes?.length > 0
    ) {
      setSelectedEventAddonItemIds(
        eventAddonItems
          .filter(({ types }) =>
            defaultSelectedEventAddonItemTypes.includes(types[0]),
          )
          .map(({ id }) => id),
      );
      setDefaultTypesSet(true);
    }
  }, [eventAddonItems]);

  const { data: { albumFolder } = {} } = useQuery(
    AlbumFolderForGiftingEventAddonItems,
    {
      variables: {
        id: albumFolderId,
        exclude_purchased_event_addon_types: excludePurchased,
      },
      skip: !albumFolderId,
    },
  );

  const celebratedName = getCelebratedPersonName(
    albumFolder?.album_subtype,
    albumFolder?.event_settings,
    false,
    albumFolder?.account?.organization_name ?? albumFolder?.account?.name,
  );
  const possessiveCelebratedName = getCelebratedPersonName(
    albumFolder?.album_subtype,
    albumFolder?.event_settings,
    true,
    albumFolder?.account?.organization_name ?? albumFolder?.account?.name,
  );

  const [checkout] = useMutation(checkoutMutation, {
    refetchQueries: (() => {
      return [
        {
          query: AlbumFolderForGiftingEventAddonItems,
          variables: {
            id: albumFolderId,
            exclude_purchased_event_addon_types: excludePurchased,
          },
        },
        {
          query: EventAddonItems,
          variables: {
            hierarchy_item_id: albumFolderId,
          },
        },
        {
          query: AlbumFolderForContributor,
          variables: {
            id: albumFolderId,
            user_uploaded_videos: user?.id,
            include_user_uploaded_videos: !!user,
          },
        },
      ].filter(Boolean);
    })(),
  });

  const onCheckout = async ({
    token,
    albumFolderId: checkoutAlbumFolderId,
    eventType = albumFolder?.album_subtype,
    user: loggedUser = user,
    eventAddonItemIds = selectedEventAddonItemIds,
    ...passThroughArgs
  } = {}) => {
    if (submitting) {
      return;
    }

    try {
      setSubmitting(true);
      setCheckoutError('');

      const { data: { checkout: checkoutResponse } = {} } = await checkout({
        variables: {
          input: {
            account_id: account?.id ?? loggedUser?.id,
            stripe_token: token?.id,
            email_address:
              token?.email ??
              account?.email_address ??
              loggedUser?.email_address,
            promo_code: token?.promo_code,
            event_addon_items: eventAddonItemIds.map(eventAddonItemId => ({
              event_addon_item_id: eventAddonItemId,
              quantity: 1,
              album_folder_id: checkoutAlbumFolderId,
            })),
            platform: 'WEB',
          },
        },
      });

      const purchasedGiftsEventAddonItems = eventAddonItems.filter(({ id }) =>
        selectedEventAddonItemIds.includes(id),
      );

      if (purchasedGiftsEventAddonItems.length > 0) {
        const transactionEventAddonTypes = uniqBy(
          sortEventAddonItemsByPriority(purchasedGiftsEventAddonItems)
            .map(({ types }) => types)
            .reduce((currentTypes, types) => [...currentTypes, ...types], []),
        );

        if (token?.promo_code === 'GOLD2FOR1') {
          gtm.trackCustomEvent({
            event: 'Bundle_Purchased',
            consumer_subtype: eventType ?? 'CUSTOM',
            user_id: account?.id ?? user?.id,
            price: formatPriceForDisplay(checkoutResponse?.total_price?.value),
          });
        } else {
          transactionEventAddonTypes.forEach(transactionEventAddonType => {
            const gtmEvent = (() => {
              if (
                transactionEventAddonType === EVENT_ADDON_ITEM_TYPE.WALDOFIED
              ) {
                return 'Waldofied_Purchased';
              }

              if (
                transactionEventAddonType === EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK
              ) {
                return 'Memories_Slideshow_Purchased';
              }

              if (
                [ALBUM_SUBTYPE.WEDDINGS, ALBUM_SUBTYPE.MEMORIALS].includes(
                  eventType,
                )
              ) {
                return 'Video_Guestbook_Purchased';
              }

              return 'Group_Video_Purchased';
            })();
            // Replacing here for gtag due to it being the only one able to use the
            // inherent options of Google Analytics (modify events & custom events)
            gtm.trackCustomEvent({
              event: gtmEvent,
              consumer_subtype: eventType ?? 'CUSTOM',
              user_id: account?.id ?? user?.id,
              price: formatPriceForDisplay(
                checkoutResponse?.total_price?.value /
                  transactionEventAddonTypes.length,
              ),
            });
          });
        }
      }

      setSubmitting(false);
      setSuccess(true);
      setSelectedEventAddonItemIds([]);
      onCheckoutSuccess?.({
        user: loggedUser,
        ...passThroughArgs,
      });
    } catch (error) {
      setCheckoutError(error);
      onCheckoutError?.({ error, ...passThroughArgs });
      sentryCaptureMessage(error, { level: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const onEventAddonItemToggle = eventAddonItemId => {
    if (selectedEventAddonItemIds.includes(eventAddonItemId)) {
      setSelectedEventAddonItemIds(
        selectedEventAddonItemIds.filter(id => id !== eventAddonItemId),
      );
    } else {
      setSelectedEventAddonItemIds([
        ...selectedEventAddonItemIds,
        eventAddonItemId,
      ]);
    }
  };

  const { giftable_event_addon_types: giftableEventAddonTypes } =
    albumFolder ?? {};

  return {
    checkoutError,
    submitting,
    success,
    onCheckout,
    eventAddonItems: eventAddonItems.filter(
      item =>
        // Ensures we always have the pre-selected item if required
        defaultSelectedEventAddonItemIds.includes(item.id) ||
        (giftableEventAddonTypes ?? []).includes(item.types[0]),
    ),
    loadingEventAddonItems,
    selectedEventAddonItemIds,
    setSelectedEventAddonItemIds,
    onEventAddonItemToggle,
    celebratedName,
    possessiveCelebratedName,
  };
};

export default useGiftingEventAddonPurchase;
